import { Backdrop, Button, Divider, Drawer, DrawerProps, Paper, Stack, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import ConfirmationDialog from '../../../ConfirmationDialog/ConfirmationDialog';
import { DateInfo } from './components/DateInfo';
import { DescriptionSection } from './components/DescriptionSection/DescriptionSection';
import { DueDateSection } from './components/DueDateSection';
import { StatusPrioritySection } from './components/StatusPrioritySection';
import { TitleSection } from './components/TitleSection';
import { TodoHeader } from './components/TodoHeader';
import { useTodoDetail } from './hooks/useTodoDetail';
import LocationSection from './components/LocationSection';

interface TodoDetailProps {
    id?: string | null;
    onClose: () => void;
    sticky?: boolean;
    drawerProps?: DrawerProps;
}

const TodoDetail = ({ id: passId, onClose, sticky = false, }: TodoDetailProps) => {
    const {
        todo,
        isEditing,
        setIsEditing,
        editedText,
        setEditedText,
        editedPriority,
        setEditedPriority,
        editedDueDate,
        setEditedDueDate,
        editedDescription,
        setEditedDescription,
        handleSaveChanges,
        updateDescription,
        handleComplete,
        resetForm
    } = useTodoDetail(passId);

    const theme = useTheme();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    if (!todo) return null;

    const isFormChanged = () => {
        const isDueDateChanged = todo.dueDate
            ? editedDueDate?.toISOString() !== dayjs(todo.dueDate).toISOString()
            : !!editedDueDate;

        return editedText !== todo.text ||
            editedPriority !== todo.priority ||
            isDueDateChanged ||
            editedDescription !== (todo.description || '');
    };

    const handleClose = () => {
        if (isEditing) {
            if (isFormChanged())
                setShowDeleteConfirm(true);
            else
                setIsEditing(false);
            return;
        }
        if (sticky) return;
        setIsEditing(false);
        onClose();
    };

    const handleCancelEditing = (toIsEditing: boolean = false) => {
        if (isEditing && !toIsEditing && isFormChanged())
            setShowDeleteConfirm(true);
        else
            setIsEditing(toIsEditing);
    };

    const handleCancelEditingConfirm = () => {
        resetForm();
        setIsEditing(false);
        setShowDeleteConfirm(false);
    };

    return (
        <>
            <Backdrop
                open={sticky && isEditing}
                sx={{
                    zIndex: theme.zIndex.drawer - 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
                onClick={handleClose}
                data-testid="dialog-backdrop"
            />
            <Drawer
                anchor="right"
                open={!!passId}
                variant={sticky ? "permanent" : "temporary"}
                onClose={handleClose}
                sx={{
                    width: sticky ? 0 : 1,
                    boxShadow: sticky ? '0 20px 10px 5px rgba(0, 0, 0, 0.2)' : undefined,
                    [theme.breakpoints.up('lg')]: {
                        width: sticky ? 400 : 600,
                    },
                    [theme.breakpoints.up('xl')]: {
                        width: 600,
                    },
                    zIndex: theme.zIndex.drawer,
                    '& .MuiDrawer-paper': {
                        width: 1,
                        position: 'fixed',
                        [theme.breakpoints.up('sm')]: {
                            width: 400,
                        },
                        [theme.breakpoints.up('md')]: {
                            width: sticky ? 600 : 600,
                        },
                        [theme.breakpoints.up('lg')]: {
                            width: sticky ? '100%' : 600,
                            position: sticky ? 'sticky' : 'fixed',
                        },
                    },
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        height: '100%',
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: 'none',
                        overflow: 'auto',
                        px: 2.5,
                        pb: 8,
                        position: 'relative',
                    }}
                >
                    <TodoHeader
                        todo={todo}
                        onClose={onClose}
                        isCompleted={todo.completed}
                        onComplete={handleComplete}
                        isEditing={isEditing}
                        setIsEditing={handleCancelEditing}
                    />

                    <Stack
                        spacing={3}
                        sx={{
                            flex: 1,
                            width: '100%',
                            position: "relative",
                        }}
                    >
                        <TitleSection
                            isEditing={isEditing}
                            editedText={editedText}
                            setEditedText={setEditedText}
                            text={todo.text}
                        />

                        <StatusPrioritySection
                            isEditing={isEditing}
                            todo={todo}
                            editedPriority={editedPriority}
                            setEditedPriority={setEditedPriority}
                        />

                        <DueDateSection
                            isEditing={isEditing}
                            editedDueDate={editedDueDate}
                            setEditedDueDate={setEditedDueDate}
                            dueDate={todo.dueDate}
                            isCompleted={todo.completed}
                        />

                        <LocationSection
                            isEditing={isEditing}
                            location={todo.location}
                        />

                        <DescriptionSection
                            isEditing={isEditing}
                            editedDescription={editedDescription}
                            setEditedDescription={setEditedDescription}
                            updateDescription={updateDescription}
                            description={todo.description}
                        />
                    </Stack>
                </Paper>
                <Stack sx={{
                    position: 'sticky',
                    bottom: 0,
                    left: '20px',
                    right: '20px',
                    pb: 2,
                    bgcolor: 'background.paper',
                }}>
                    <Divider sx={{ mb: 2 }} />
                    {isEditing && (
                        <Stack
                            direction="row"
                            gap={2}
                            justifyContent="flex-end"
                            width={1}
                            mb={3}
                        >
                            <Button variant="outlined" onClick={() => handleCancelEditing()}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={handleSaveChanges}>
                                Save Changes
                            </Button>
                        </Stack>
                    )}

                    <Stack px={2}>
                        <DateInfo createdAt={todo.createdAt} updatedAt={todo.updatedAt} />
                    </Stack>
                </Stack>
                <ConfirmationDialog
                    title="Cancel Editing"
                    message="Are you sure you want to cancel editing? Your changes will be lost."
                    confirmText="Confirm"
                    cancelText="Back to Editing"
                    severity="warning"
                    open={showDeleteConfirm}
                    onConfirm={handleCancelEditingConfirm}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            </Drawer>
        </>
    );
};

export default TodoDetail; 